import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import {
  FiFileText,
  FiShield,
  FiCalendar,
  FiAlertCircle,
  FiArrowRight,
} from 'react-icons/fi';

const LoadingSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    {/* Header Skeleton */}
    <div className="flex items-center justify-between">
      <div className="h-8 w-48 bg-secondary-light dark:bg-secondary-dark rounded"></div>
    </div>

    {/* Report Type Cards Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div 
          key={i}
          className="bg-primary-light dark:bg-primary-dark p-6 rounded-xl shadow-lg border border-secondary-light dark:border-secondary-dark"
        >
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-secondary-light dark:bg-secondary-dark rounded-full"></div>
            <div className="flex-1">
              <div className="h-5 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            </div>
          </div>
          <div className="h-4 w-3/4 bg-secondary-light dark:bg-secondary-dark rounded mb-3"></div>
          <div className="h-4 w-1/2 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
      ))}
    </div>
  </div>
);

const CreateReportSelection = () => {
  const { role } = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Simulate loading for demo
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="p-4 page-fade-in">
        <LoadingSkeleton />
      </div>
    );
  }

  if (role !== 'admin' && role !== 'trainer') {
    return (
      <div className="p-4 sm:p-6 min-h-[75vh] bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark flex items-center justify-center page-fade-in">
        <div className="text-center p-6 sm:p-8 bg-primary-light dark:bg-primary-dark rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark">
          <FiAlertCircle className="text-4xl sm:text-6xl mb-4 text-red-500 mx-auto" />
          <h2 className="text-xl sm:text-3xl font-semibold mb-2">Access Denied</h2>
          <p className="text-sm sm:text-lg text-text-light/70 dark:text-text-dark/70">
            You do not have permission to access this page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark rounded-lg hover:brightness-110 transition-all duration-300 text-sm sm:text-base"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const handleSelection = (path) => {
    navigate(path);
  };

  const mainCardStyle = `p-4 sm:p-6 lg:p-8 rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark transition-colors duration-300`;

  const cardStyle = `
    relative
    p-4 sm:p-6 
    rounded-lg 
    shadow-md 
    bg-secondary-light dark:bg-secondary-dark 
    border border-secondary-light dark:border-secondary-dark 
    transition-all duration-300
    hover:-translate-y-1 hover:shadow-xl
    hover:bg-secondary-light/90 dark:hover:bg-secondary-dark/90
    cursor-pointer
    group
  `;

  const reportTypes = [
    {
      path: '/create-dor',
      icon: FiFileText,
      title: 'Daily Observation Report',
      description: 'Record daily observations for trainees.',
    },
    {
      path: '/create-officer-safety-report',
      icon: FiShield,
      title: 'Officer Safety Report',
      description: 'Report any officer safety incidents.',
    },
    {
      path: '/create-end-of-week-report',
      icon: FiCalendar,
      title: 'End of Week Report',
      description: "Summarize the week's training progress.",
    },
  ];

  return (
    <div className="min-h-[75vh] p-3 sm:p-4 lg:p-6 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300 page-fade-in">
      <div className="max-w-7xl mx-auto">
        <div className={mainCardStyle}>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 lg:mb-8 flex items-center">
            <FiFileText className="mr-2 sm:mr-3 w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
            Create a Report
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {reportTypes.map((report, index) => (
              <div
                key={index}
                onClick={() => handleSelection(report.path)}
                className={cardStyle}
              >
                <div className="flex flex-col items-center text-center">
                  <report.icon className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 text-accent-light dark:text-accent-dark transition-transform duration-300 group-hover:scale-110" />
                  <h2 className="text-base sm:text-xl lg:text-2xl font-semibold mb-2">
                    {report.title}
                  </h2>
                  <p className="text-xs sm:text-sm lg:text-base text-text-light/70 dark:text-text-dark/70">
                    {report.description}
                  </p>
                  
                  {/* Arrow indicator */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                    <FiArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-accent-light dark:text-accent-dark" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Back button */}
        <div className="mt-4 sm:mt-6">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark rounded-lg hover:bg-secondary-light/80 dark:hover:bg-secondary-dark/80 transition-all duration-300"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateReportSelection;
